/**
 * Quill clipboard that imports nested list HTML with the correct indent per item.
 *
 * Quill 1's own `li` matcher puts the item's indent on the last newline of the
 * item's delta. When an item holds text and then a nested list, that last newline
 * belongs to the deepest nested item, so the item itself loses its level and the
 * nested item gets the parent's level. The editor emits nested lists
 * (`nestQuillListHtml.ts`), and pasted content from mail or document editors is
 * nested too, so every reload and paste needs the correct import.
 *
 * 1. `matchListIndent` counts the lists between the item and the clipboard root.
 * 2. It finds the item's own newline: the first newline that no nested item has
 *    claimed. Nested items run first, and each sets `indent` on its own newline.
 * 3. It sets `indent` on that newline. Depth 0 leaves the delta unchanged.
 * 4. `registerNestedListClipboard` installs a Clipboard subclass that replaces
 *    Quill's `li` matcher with this one. Matchers are selected by selector, so a
 *    minified Quill build cannot break the replacement.
 *
 * Continues in `registerQuillModules.ts`.
 */
import { Quill } from 'react-quill';
import { z } from 'zod';

const CLIPBOARD_MODULE_NAME = 'modules/clipboard';
const CLIPBOARD_ROOT_CLASS = 'ql-clipboard';
const LIST_ITEM_SELECTOR = 'li';
const LIST_SELECTOR = 'ul, ol';
const NEWLINE = '\n';

/** The part of a Quill delta op this matcher reads. Embeds have an object `insert`. */
type DeltaOp = { insert?: unknown; attributes?: { indent?: unknown } };

/** The part of a Quill delta this matcher uses. */
type QuillDelta = { ops: DeltaOp[]; compose: (other: QuillDelta) => QuillDelta };

/** A clipboard matcher as Quill stores it: a selector and the function it runs. */
type ClipboardMatcher = [string | number, (node: Node, delta: QuillDelta) => QuillDelta];

const textInsert = z.string();

/** Counts the lists between the item and the clipboard root. A top-level item has depth 0. */
const listDepth = (item: HTMLLIElement): number => {
  let depth = -1;
  let parent = item.parentElement;
  while (parent !== null && !parent.classList.contains(CLIPBOARD_ROOT_CLASS)) {
    if (parent.matches(LIST_SELECTOR)) {
      depth += 1;
    }
    parent = parent.parentElement;
  }
  return depth;
};

/**
 * Finds the offset of the item's own newline: the first newline in an op with no
 * `indent`. Every nested item already set `indent` on its own newline, and ops
 * only merge when their attributes match, so a nested newline never shares an op
 * with the item's own text.
 */
const findOwnNewlineOffset = (ops: DeltaOp[]): number | undefined => {
  let offset = 0;
  for (const op of ops) {
    const text = textInsert.safeParse(op.insert);
    if (!text.success) {
      offset += 1;
      continue;
    }
    const index = text.data.indexOf(NEWLINE);
    if (index >= 0 && op.attributes?.indent === undefined) {
      return offset + index;
    }
    offset += text.data.length;
  }
  return undefined;
};

/** Sets `indent` on the list item's own newline from its list depth. */
export const matchListIndent = (node: Node, delta: QuillDelta): QuillDelta => {
  if (!(node instanceof HTMLLIElement)) {
    return delta;
  }
  const indent = listDepth(node);
  if (indent <= 0) {
    return delta;
  }
  const offset = findOwnNewlineOffset(delta.ops);
  if (offset === undefined) {
    return delta;
  }
  const Delta = Quill.import('delta');
  return delta.compose(new Delta().retain(offset).retain(1, { indent }));
};

/**
 * Replaces Quill's clipboard module with one whose `li` matcher is `matchListIndent`.
 * `Quill.register(..., true)` overwrites the existing module, so a second call from
 * the other editor's `registerQuillModules` is safe.
 */
export const registerNestedListClipboard = (): void => {
  const Clipboard = Quill.import(CLIPBOARD_MODULE_NAME);

  /**
   * Quill's clipboard with the default `li` matcher swapped for `matchListIndent`.
   * Quill fills `this.matchers` in the base constructor, so the swap has to happen
   * here, after `super`, and not through `Clipboard.DEFAULTS`. The filter keys on
   * the `li` selector, which a minified Quill build keeps.
   */
  class NestedListClipboard extends Clipboard {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(quill: any, options: any) {
      super(quill, options);
      const matchers: ClipboardMatcher[] = this.matchers;
      this.matchers = matchers.filter(([selector]) => selector !== LIST_ITEM_SELECTOR);
      this.addMatcher(LIST_ITEM_SELECTOR, matchListIndent);
    }
  }

  Quill.register(CLIPBOARD_MODULE_NAME, NestedListClipboard, true);
};
